import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useGetIntakeOrgServicesQuery, useSetIntakeOrgServicesMutation } from '../store/api';

import {
  INTAKE_SERVICE_OPTIONS,
  IntakeServiceSelectionCard,
  type SelectableIntakeService,
} from './IntakeServiceSelectionCard';

export function ProviderIntakeServices({
  onSaved,
  standalone = false,
  failureCount,
  onFailureCountChange,
}: {
  onSaved?: () => Promise<unknown>;
  standalone?: boolean;
  failureCount?: number;
  onFailureCountChange?: (count: number) => void;
}) {
  const {
    currentData: data,
    error: loadError,
    isFetching,
    refetch,
  } = useGetIntakeOrgServicesQuery();
  const [setServices] = useSetIntakeOrgServicesMutation();
  const { intakeHub } = useEventTracking();
  const saving = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingServices, setPendingServices] = useState<SelectableIntakeService[] | null>(null);
  const [error, setError] = useState<string>();
  const [localFailedCount, setFailedCount] = useState(0);
  const failedCount = failureCount ?? localFailedCount;
  const [confirmation, setConfirmation] = useState<SelectableIntakeService[] | null>(null);
  const services = INTAKE_SERVICE_OPTIONS.filter(option =>
    data?.service_keys?.includes(option.id),
  ).map(option => option.id);

  const save = async (next: SelectableIntakeService[], confirmRemove = false) => {
    if (!data?.can_manage_services || loadError || saving.current) return;
    saving.current = true;
    setIsSaving(true);
    setPendingServices(next);
    setError(undefined);
    intakeHub.serviceSelectionChanged({
      service_keys: next.join(','),
      selected_count: next.length,
    });
    let persisted = false;
    try {
      const result = await setServices({
        organizationEnrollServicesRequest: {
          service_keys: next,
          confirm_remove: confirmRemove,
        },
      }).unwrap();
      persisted = true;
      setConfirmation(null);
      const failures = result.failed_user_ids?.length ?? 0;
      setFailedCount(failures);
      onFailureCountChange?.(failures);
      await refetch().unwrap();
      await onSaved?.();
    } catch (cause) {
      const response =
        cause && typeof cause === 'object' && 'data' in cause ? cause.data : undefined;
      const code =
        response && typeof response === 'object' && 'error_code' in response
          ? response.error_code
          : undefined;
      if (code === 'intake_service_removal_unconfirmed') {
        setConfirmation(next);
      } else {
        setConfirmation(null);
        const conflict =
          cause && typeof cause === 'object' && 'status' in cause && cause.status === 409;
        setError(
          persisted
            ? 'Your services were saved, but we could not refresh this view. Reload to see your selection.'
            : conflict
              ? 'Services changed while you were saving. Review the current selection and try again.'
              : code === 'intake_service_selection_empty'
                ? 'Choose at least one service to start intake for your practice.'
                : 'We could not save your services. Reload the current selection and try again.',
        );
        await refetch();
      }
    } finally {
      saving.current = false;
      setIsSaving(false);
      setPendingServices(null);
    }
  };
  return (
    <Box sx={standalone ? { maxWidth: 600, mx: 'auto', px: 3, py: 6 } : undefined}>
      <Stack spacing={2}>
        {loadError ? (
          <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>
            We could not load your organization&apos;s services. Changes are disabled until they
            load.
          </Alert>
        ) : !data ? (
          <LinearProgress aria-label="Loading organization services" />
        ) : null}
        {data && (
          <IntakeServiceSelectionCard
            services={pendingServices ?? services}
            canManage={Boolean(data.can_manage_services) && !loadError}
            isSaving={isSaving || isFetching}
            onChange={next => void save(next)}
          />
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {failedCount > 0 && (
          <Alert severity="warning">
            Services were saved, but {failedCount} provider journey{failedCount === 1 ? '' : 's'}{' '}
            could not be updated.
            <Button disabled={isSaving} onClick={() => void save(services)}>
              Retry provider setup
            </Button>
          </Alert>
        )}
      </Stack>
      <Dialog open={confirmation !== null} onClose={() => !isSaving && setConfirmation(null)}>
        <DialogTitle>Remove services already used by your practice?</DialogTitle>
        <DialogContent>
          This selection removes a service your providers already use. Its questions will be hidden
          and existing answers kept.
        </DialogContent>
        <DialogActions>
          <Button disabled={isSaving} onClick={() => setConfirmation(null)}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={() => confirmation && void save(confirmation, true)}>
            Confirm removal
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
